import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  getPaymentHistories,
  getPaymentHistoryState,
  IPaymentHistory,
  resetPaymentHistoryListData,
  TGetPaymentHistories,
} from '../../../../store/V2/PaymentHistorySlice';
import {
  getStripeCustomerPortalData,
  resetStripeCustomerPortalData,
  getStripeV2State,
} from '../../../../store/V2/StripeSlice';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../components/V2/Table/type1/TableHeadCustom';
import { useAppSelector } from '../../../../hooks';
import TablePaginationActions from '../../../../components/V2/Table/type1/TablePaginationActions';
import PaymentHistoryTableRow from './PaymentHistoryTableRow';
import TableNoData from '../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../components/Loaders/TableLoader';
import { TPTextField } from '../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';

type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultListLimit = 30;
const defaultOrderBy = 'transactionDate';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  offset: 0,
  limit: defaultListLimit,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const defaultState: TState = {
  startDate: null,
  endDate: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'transactionDate',
    label: 'Date',
    sortable: true,
  },
  {
    id: 'method',
    label: 'Method',
    sortable: false,
  },
  {
    id: 'planMinutes',
    label: 'Plan Hours',
    sortable: true,
  },
  {
    id: 'amount',
    label: 'Amount',
    sortable: true,
  },
  {
    id: 'reference',
    label: 'Reference',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
];

const PaymentHistory: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const paymentHistoryState = useAppSelector(getPaymentHistoryState);
  const stripeState = useAppSelector(getStripeV2State);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });

  let paymentHistoryList: IPaymentHistory[] = [];
  let totalCount = 0;

  if (paymentHistoryState.paymentHistoryListData) {
    totalCount = paymentHistoryState.paymentHistoryListData.totalDocs || 0;

    if (
      paymentHistoryState.paymentHistoryListData.list &&
      paymentHistoryState.paymentHistoryListData.list.length
    ) {
      paymentHistoryList =
        paymentHistoryState.paymentHistoryListData.list || [];
    }
  }

  useEffect(() => {
    getPaymentHistoryListData({ ...defaultFilterData });

    return () => {
      dispatch(resetPaymentHistoryListData());
      dispatch(resetStripeCustomerPortalData());
    };
  }, []);

  useEffect(() => {
    if (
      stripeState.stripeCustomerPortalData &&
      stripeState.stripeCustomerPortalData.url
    ) {
      window.location.href = stripeState.stripeCustomerPortalData.url;
    }
  }, [stripeState.stripeCustomerPortalData]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getPaymentHistoryListData = (filterDataAttributes: TFilterData) => {
    const apiData: TGetPaymentHistories = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    dispatch(getPaymentHistories({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getPaymentHistoryListData({
      ...filterData,
      sortKey: id,
      sortValue: newSortValue,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getPaymentHistoryListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getPaymentHistoryListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleManageSubscriptions = () => {
    dispatch(getStripeCustomerPortalData());
  };

  return (
    <Box>
      <div>
        <Stack direction="row" justifyContent="flex-end">
          <DarkLoadingButton
            variant="contained"
            color="inherit"
            sx={{ ...defaultButtonStyles }}
            onClick={handleManageSubscriptions}
          >
            Manage Subscriptions
          </DarkLoadingButton>
        </Stack>
        <div style={{ paddingBottom: '24px' }}>
          <Typography variant="h6" fontSize="18px">
            Payment History
          </Typography>
        </div>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div style={{ paddingBottom: '20px' }}>
            <Grid container columnSpacing="16px" rowSpacing="20px">
              <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth>
                  <DatePicker
                    clearable
                    value={state.startDate}
                    label="Start Date"
                    inputFormat="YYYY-MM-DD"
                    onChange={(date) => {
                      updateStateAttributes({
                        startDate: date,
                      });

                      getPaymentHistoryListData({
                        ...filterData,
                        startDate: date
                          ? date.clone().startOf('day').toISOString()
                          : undefined,
                      });
                    }}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="Start Date"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth>
                  <DatePicker
                    clearable
                    value={state.endDate}
                    label="End Date"
                    inputFormat="YYYY-MM-DD"
                    onChange={(date) => {
                      updateStateAttributes({
                        endDate: date,
                      });

                      getPaymentHistoryListData({
                        ...filterData,
                        endDate: date
                          ? date.clone().endOf('day').toISOString()
                          : undefined,
                      });
                    }}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="End Date"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </LocalizationProvider>
        <div style={{ paddingBottom: '14px' }}>
          <TableContainer
            sx={{
              position: 'relative',
              maxHeight: 'calc(100vh - 250px)',
              maxWidth: '1000px',
              minHeight: '400px',
            }}
          >
            <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
              <TableHeadCustom
                rowCount={0}
                order={tableData.order}
                orderBy={tableData.orderBy}
                headLabels={tableHeaderLabels}
                numSelected={tableData.selected.length}
                onSort={handleSort}
              />
              <TableBody>
                {paymentHistoryList.map((row) => (
                  <PaymentHistoryTableRow key={row._id} paymentHistory={row} />
                ))}

                {paymentHistoryState.status !== 'loading' &&
                !paymentHistoryList.length ? (
                  <TableNoData title="No payment histories are available" />
                ) : null}

                {paymentHistoryState.status === 'loading' &&
                !paymentHistoryList.length ? (
                  <TableLoader />
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={defaultRowsPerPageOptions}
              component="div"
              count={totalCount}
              rowsPerPage={tableData.rowsPerPage}
              page={tableData.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              ActionsComponent={TablePaginationActions}
            />

            <FormControlLabel
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
              control={
                <Switch
                  checked={tableData.dense}
                  onChange={tableData.onChangeDense}
                />
              }
            />
          </Box>
        </div>
      </div>
    </Box>
  );
};

export default PaymentHistory;
